import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Eye, CalendarDays, MessageCircle, CheckCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aurobindo-kids/artifacts/z058rndb_Aurobindo_Logo_WhiteBackgnd.png';
const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20visiting%20Sri%20Aurobindo%20Montessori%20School';

const IMG_STORY    = 'https://images.unsplash.com/photo-1777056481869-feac70afe522?w=900&q=85';
const IMG_FOUNDER  = 'https://images.unsplash.com/photo-1590650213165-c1fef80648c4?w=600&q=85';
const IMG_TEAM     = 'https://images.unsplash.com/photo-1722247479528-15f9b03ab3d8?w=1000&q=85';
const IMG_CHILDREN = 'https://images.unsplash.com/photo-1780844824578-d0b1a5b4b329?w=700&q=85';

const chapters = [
  {
    num: '01',
    title: 'The Primary years',
    period: '2000 onwards',
    desc: 'We began with one Primary environment and a small band of trained teachers, at a time when very few in Hyderabad had heard the word "Montessori".',
  },
  {
    num: '02',
    title: 'Growing with the children',
    period: 'Mid-2000s',
    desc: 'As our first children turned six, our founder trained in the Montessori Elementary method so the journey would not stop at preschool. In 2009, we helped bring the city\'s Montessori community together by hosting the Hyderabad Montessori Schools Expo.',
  },
  {
    num: '03',
    title: 'Deepening, not just widening',
    period: 'Today',
    desc: 'We now run three Primary environments and a full Elementary program, with 92 children and 10 teachers. Dozens of educators who trained and worked here have gone on to start Montessori schools of their own. We consider that part of our mission.',
  },
];

const teamStats = [
  { number: '7 of 10', label: 'Teachers are AMI-trained', sub: 'Assoc. Montessori Internationale' },
  { number: '2', label: 'AMI guides per environment', sub: 'Beyond the Montessori norm of one' },
  { number: '55+', label: 'Years of cumulative experience', sub: 'Across the teaching team' },
  { number: '92', label: 'Children in school today', sub: '3 Primary + 1 Elementary env.' },
];

const parentPoints = [
  {
    icon: <Eye size={20} className="text-[#1E3A8A]" />,
    title: 'Parent observation days, twice a year',
    desc: 'You sit quietly in the environment and watch your own child at work — no commentary, no interruption. Just seeing.',
  },
  {
    icon: <CalendarDays size={20} className="text-[#1E3A8A]" />,
    title: 'Observation by appointment, any time',
    desc: 'Book a day in advance and come whenever you like throughout the year. You are always welcome.',
  },
  {
    icon: <MessageCircle size={20} className="text-[#1E3A8A]" />,
    title: 'Open conversation at every stage',
    desc: 'From the first tour onwards, ask us anything. There are no silly questions when it comes to your child.',
  },
];

export default function About() {
  return (
    <div className="pt-16">
      <Helmet>
        <title>About Us | 25+ Years of Authentic Montessori in Hyderabad — Sri Aurobindo Montessori</title>
        <meta name="description" content="Founded in 2000 by an educator with B.Sc, B.Ed, M.Ed, M.Phil and AMI Montessori training, our school in West Marredpally, Secunderabad has guided 1200+ children — growing purely through parents' word of mouth." />
      </Helmet>

      {/* ── BANNER / H1 ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">About Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            A school that began<br className="hidden sm:block" /> with one question
          </h1>
          <p className="text-blue-200 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto italic">
            "Why should children sit still, copying from a board, when they are built to move, touch, question and discover?"
          </p>
        </div>
      </section>

      {/* ── OUR STORY ── */}
      <section className="py-20 bg-white" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Our Story</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Our story
              </h2>
              <div className="space-y-5 text-slate-600 leading-relaxed text-base">
                <p>
                  Our founder, Ms. Sujatha, spent thirteen years teaching in reputed schools of Hyderabad — and something never sat right. Bright, curious children spent their days reading and writing continuously, waiting for instructions, learning to follow rather than to think.
                </p>
                <p>
                  Then she encountered the work of Dr. Maria Montessori — Italy's first woman doctor, a physician and psychologist who built an entire method of education around how children actually develop. It changed everything.
                </p>
                <p>
                  She went on to complete her B.Sc, B.Ed, M.Ed and M.Phil, then trained formally in both Montessori Primary and Elementary methods, and in the year 2000 opened this school with a simple, stubborn conviction: <strong className="text-[#1E3A8A]">make the child independent, and everything else follows.</strong>
                </p>
                <p>
                  Twenty-five years and more than 1200 children later, that conviction hasn't moved an inch. Neither has our other founding decision: we have never advertised. No pamphlets, no hoardings. Every single family has come to us through the word of another happy family — mostly from within 4–5 km of the school. We think that says more than any brochure could.
                </p>
              </div>

              {/* Philosophy callout */}
              <div className="mt-10 bg-[#EFF6FF] rounded-3xl px-8 py-6 border-l-4 border-[#1E3A8A]">
                <p className="text-[#1E3A8A] font-black text-xl sm:text-2xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  "Hands to work.&nbsp; Mind to think.&nbsp; Heart to love."
                </p>
                <p className="text-slate-400 text-sm mt-2">Our founding philosophy — unchanged since 2000.</p>
              </div>
            </div>

            {/* Story image + children image stacked */}
            <div className="space-y-5">
              <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)]">
                <img src={IMG_STORY} alt="Children in the Montessori classroom" className="w-full h-72 object-cover" loading="lazy" />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.10)]">
                <img src={IMG_CHILDREN} alt="Students drawing together" className="w-full h-52 object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOUNDER'S NOTE ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="founder-note-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">

            {/* Founder image */}
            <div className="lg:col-span-2 flex justify-center lg:justify-start">
              <div className="rounded-3xl overflow-hidden shadow-[0_12px_48px_rgba(30,58,138,0.15)]" style={{ maxWidth: '320px', width: '100%' }}>
                <img
                  src={IMG_FOUNDER}
                  alt="Ms. Sujatha Reddy, Founder"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Note text */}
            <div className="lg:col-span-3">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">In her own words</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Welcome Note from the Founder's Desk
              </h2>

              {/* Letter card */}
              <div className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(30,58,138,0.08)] relative">
                <div className="text-5xl text-[#DBEAFE] font-black leading-none mb-4">"</div>
                <div className="space-y-4 text-slate-700 leading-relaxed italic text-base">
                  <p>
                    When I began teaching, something always troubled me. Bright, curious children spent their days copying from a board, waiting to be told what to do. Then I discovered Dr. Montessori's work, and everything changed.
                  </p>
                  <p>
                    I realized that when children are given real freedom — the freedom to choose, to think, and to work independently — they don't become chaotic. They become focused, confident, and capable. They learn to make decisions, take responsibility, and discover the joy of learning for themselves.
                  </p>
                  <p>
                    That is why this school exists.
                  </p>
                  <p>
                    For over twenty-five years, my mission has remained the same: to provide every child with a strong foundation during the most important years of life. We strive to nurture children who are independent, compassionate, confident, and prepared for the future.
                  </p>
                  <p>
                    Hands to work, mind to think, heart to love — this has always been our guiding philosophy.
                  </p>
                  <p>
                    I warmly invite you to visit our school. Spend some time observing the children at work, experience the peaceful atmosphere, and see Montessori education in action. Like so many families before you, I hope you will feel that this is truly a home away from home.
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-blue-50">
                  <p className="font-bold text-[#1E3A8A] text-sm not-italic">Ms. Sujatha Reddy</p>
                  <p className="text-slate-400 text-xs not-italic">Founder, Sri Aurobindo Montessori House of Children School</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── OUR IDENTITY — Logo Showcase (kept from original) ── */}
      <section className="bg-[#1E3A8A] py-20" data-testid="identity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            <div className="flex justify-center lg:justify-end">
              <div
                className="rounded-3xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.30)]"
                style={{ width: '320px', height: '280px' }}
              >
                <img
                  src={LOGO_URL}
                  alt="Sri Aurobindo Montessori School Logo"
                  data-testid="identity-logo"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center', transform: 'scale(1.18)', transformOrigin: 'center' }}
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Our Identity</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Why "Sri Aurobindo"?
              </h2>
              <p className="text-blue-200 text-base mb-2 leading-relaxed">
                The name is a promise, not a decoration.
              </p>
              <p className="text-blue-200 text-base mb-8 leading-relaxed">
                Early in her career, a mentor's words changed our founder's path: <em>we should not think only of ourselves; we should look to help others.</em> When she later studied Sri Aurobindo's philosophy — that true education draws out what is already within the child, and serves their inner growth, not just their marks — she knew she had found the name for her school.
              </p>
              <div className="bg-white/10 rounded-2xl px-6 py-5 border border-white/20">
                <p className="text-white font-semibold text-sm leading-relaxed">
                  Sri Aurobindo's vision and Dr. Montessori's method meet in the same place: <strong className="font-black">respect for the child as a complete human being.</strong>
                </p>
              </div>

              <div className="mt-8 space-y-5">
                {[
                  { title: 'The Geometric Frame', desc: 'A lattice of triangles and hexagons — representing the prepared Montessori environment where structure and freedom coexist.' },
                  { title: 'The Lotus on Water', desc: 'Rising from still water, the lotus is Sri Aurobindo\'s symbol — the pure potential within every child, capable of extraordinary growth when nurtured.' },
                  { title: 'Deep Blue & Pure White', desc: 'Blue conveys depth, calm, and trust. White represents clarity, openness, and the unbounded potential of every young mind.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-1 self-stretch rounded-full bg-blue-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THREE CHAPTERS ── */}
      <section className="py-20 bg-white" data-testid="chapters-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Since 2000</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Our journey in three chapters
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {chapters.map((ch, i) => (
              <div
                key={i}
                data-testid={`chapter-card-${i}`}
                className="relative bg-[#F4F7FB] rounded-3xl p-8 hover:-translate-y-1 transition-all duration-300"
              >
                <span
                  className="text-7xl font-black leading-none select-none absolute top-5 right-6 text-[#DBEAFE]"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {ch.num}
                </span>
                <div className="relative z-10">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-2">{ch.period}</p>
                  <h3 className="text-xl font-extrabold text-[#1E3A8A] mb-4 leading-snug" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {ch.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{ch.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE TEAM ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-3">Our People</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                The team behind the children
              </h2>
              <div className="space-y-4 text-blue-100 text-base leading-relaxed">
                <p>
                  Finding genuinely trained Montessori teachers is one of the hardest parts of running an authentic school — AMI training is respected worldwide, and many who earn it move abroad. We have always refused to compromise.
                </p>
                <p>
                  Every environment has two AMI-trained guides — far beyond the standard Montessori norm of one trained adult per environment. The team is led by Ms. Sujatha herself, with 25 years of Montessori experience.
                </p>
                <p>
                  What makes them effective isn't only the certificate. It is <strong className="text-white">observation</strong> — every day, each guide closely observes children: is this child growing independent? Proactive? Waiting for guidance? Then, gently and indirectly, the environment is adjusted.
                </p>
                <p className="text-blue-300 text-sm italic">
                  Nobody here works "as an employee". They work for the child.
                </p>
              </div>

              {/* Team stat grid */}
              <div className="grid grid-cols-2 gap-4 mt-10">
                {teamStats.map((s, i) => (
                  <div key={i} data-testid={`team-stat-${i}`} className="bg-white/10 rounded-2xl px-5 py-5 border border-white/10">
                    <p className="text-2xl font-black text-white leading-none mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>{s.number}</p>
                    <p className="text-blue-200 text-xs font-semibold leading-snug">{s.label}</p>
                    <p className="text-blue-400 text-xs mt-0.5 leading-snug">{s.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Team image */}
            <div className="rounded-3xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.30)]">
              <img src={IMG_TEAM} alt="The Montessori classroom environment" className="w-full h-[480px] object-cover" loading="lazy" />
            </div>

          </div>
        </div>
      </section>

      {/* ── HOW WE WORK WITH PARENTS ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="parents-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Transparency</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              How we work with parents
            </h2>
            <p className="text-slate-600 text-base">
              You are not handing your child over to a black box.
            </p>
          </div>

          <div className="space-y-4">
            {parentPoints.map((pt, i) => (
              <div key={i} data-testid={`parent-point-${i}`} className="flex items-start gap-5 bg-white rounded-2xl px-7 py-6 shadow-[0_4px_20px_rgba(59,130,246,0.06)]">
                <div className="w-10 h-10 rounded-2xl bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5">
                  {pt.icon}
                </div>
                <div>
                  <p className="font-bold text-[#0F172A] text-sm mb-1">{pt.title}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHERE WE'RE HEADED ── */}
      <section className="py-20 bg-white" data-testid="future-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Looking Ahead</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Where we're headed
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed text-base">
                <p>
                  Our children currently complete their Elementary journey with us at 12, superbly prepared for any school they move to next.
                </p>
                <p>
                  And we're not done. We plan to grow the school up to <strong className="text-[#1E3A8A]">Class 10</strong>, so the Montessori journey can continue even longer — giving children the full span of their formative years in an environment built around who they actually are.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Link
                  to="/admissions#enquiry-form"
                  data-testid="about-cta-tour-btn"
                  className="bg-[#1E3A8A] text-white rounded-full px-7 py-4 font-black text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
                >
                  Meet us in person — Book a Tour <ArrowRight size={16} />
                </Link>
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-whatsapp-btn"
                  className="border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-full px-7 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              </div>
            </div>

            {/* Callout box */}
            <div className="bg-[#EFF6FF] rounded-3xl p-10 flex flex-col gap-5">
              {[
                { check: '92 children, 10 teachers, 3 Primary + 1 Elementary environment' },
                { check: 'Growing to Class 10 — the full Montessori journey' },
                { check: 'Educators trained here have started their own schools across Hyderabad' },
                { check: '25+ years with zero advertising — purely parent-to-parent' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle size={14} className="text-[#1E3A8A]" />
                  </div>
                  <p className="text-slate-700 text-sm leading-relaxed">{item.check}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
